import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/Icon';

/**
 * Чип-фильтр с мульти-выбором. Внешне идентичен FilterChip из учеников
 * (использует `.chip` / `.menu` / `.menu__item`), но клик по пункту не
 * закрывает меню — он переключает галочку.
 *
 * Props:
 *   label   — заголовок чипа (например, «Тип платежа»)
 *   values  — массив выбранных значений (string[])
 *   options — [{ v, label }]
 *   onChange(values) — отдаёт новый массив
 *   placeholder — текст когда ничего не выбрано (по умолчанию формируется из label)
 */
export default function MultiFilterChip({ label, values, options, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const valSet = new Set(values || []);
  const isActive = valSet.size > 0;

  const toggle = (v) => {
    const next = new Set(valSet);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(Array.from(next));
  };

  // Что показать в чипе после двоеточия
  const summary = (() => {
    if (!isActive) return null;
    if (valSet.size === 1) {
      const o = options.find((o) => o.v === [...valSet][0]);
      return o ? o.label : '?';
    }
    return `${valSet.size} ${pluralize(valSet.size)}`;
  })();

  return (
    <div className="chip-wrap" ref={ref}>
      <button
        type="button"
        className={'chip' + (isActive ? ' is-active' : '')}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="chip__label">{label}</span>
        {isActive && <span className="chip__sep">:</span>}
        {isActive && <span className="chip__value">{summary}</span>}
        {!isActive && placeholder && <span className="chip__sep">:</span>}
        {!isActive && placeholder && (
          <span className="chip__value" style={{ color: 'var(--fg-faint)' }}>{placeholder}</span>
        )}
        <Icon name="chevronDown" size={10} />
      </button>
      {open && (
        <div className="menu" style={{ minWidth: 240, maxHeight: 360, overflowY: 'auto' }}>
          {options.map((o) => {
            const active = valSet.has(o.v);
            return (
              <button
                key={o.v}
                type="button"
                className={'menu__item' + (active ? ' is-active' : '')}
                onClick={() => toggle(o.v)}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Checkbox checked={active} />
                  <span>{o.label}</span>
                </span>
              </button>
            );
          })}
          {isActive && (
            <>
              <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
              <button
                type="button"
                className="menu__item"
                onClick={() => onChange([])}
                style={{ color: 'var(--fg-muted)' }}
              >
                Сбросить
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Checkbox({ checked }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 14, height: 14, display: 'inline-grid', placeItems: 'center',
        border: '1px solid ' + (checked ? 'var(--green-500)' : 'var(--border-strong)'),
        background: checked ? 'var(--green-500)' : 'var(--panel)',
        color: 'white', borderRadius: 3,
        flexShrink: 0,
        transition: 'background 100ms, border-color 100ms',
      }}
    >
      {checked && <Icon name="check" size={10} />}
    </span>
  );
}

function pluralize(n) {
  const n10 = n % 10, n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return 'выбран';
  if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return 'выбрано';
  return 'выбрано';
}
